import type { InfiniteData, QueryClient, QueryKey } from "@tanstack/react-query";

import type { CustomerRow } from "@/dashboard/features/hub/lib/customer/schemas";
import type { CursorPage } from "@/dashboard/lib/query/pagination";

/**
 * Keep only the first page of a customer-list cache before an automatic refresh.
 *
 * A cursor tail is tied to the ordering and membership represented by the first page. Once a
 * list is stale, carrying that tail into a new first-page response can duplicate rows or skip
 * rows that moved across the cursor boundary. The first page's original page param is retained;
 * no page or param is fabricated when the cache is empty or already bounded.
 */
export function trimCustomerListCache(
  data: InfiniteData<CursorPage<CustomerRow>> | undefined
): InfiniteData<CursorPage<CustomerRow>> | undefined {
  if (!data || data.pages.length <= 1) {
    return data;
  }

  return {
    ...data,
    pages: data.pages.slice(0, 1),
    pageParams: data.pageParams.slice(0, 1),
  };
}

/** Trim every cached customer-list query that matches the tenant/list prefix. */
export function trimCustomerListCaches(queryClient: QueryClient, queryKey: QueryKey): void {
  const cachedQueries = queryClient.getQueriesData<InfiniteData<CursorPage<CustomerRow>>>({
    queryKey,
  });

  trimCachedQueries(queryClient, cachedQueries);
}

/** Trim one exact customer-list cache without touching sibling filters or tabs. */
export function trimCustomerListCacheAtKey(queryClient: QueryClient, queryKey: QueryKey): void {
  const data = queryClient.getQueryData<InfiniteData<CursorPage<CustomerRow>>>(queryKey);
  const trimmed = trimCustomerListCache(data);
  if (trimmed !== data) {
    queryClient.setQueryData(queryKey, trimmed);
  }
}

/** Trim list caches selected by a key predicate, for mutations that span tenant and ALL views. */
export function trimCustomerListCachesMatching(
  queryClient: QueryClient,
  predicate: (queryKey: QueryKey) => boolean
): void {
  const cachedQueries = queryClient.getQueriesData<InfiniteData<CursorPage<CustomerRow>>>({
    predicate: query => predicate(query.queryKey),
  });

  trimCachedQueries(queryClient, cachedQueries);
}

function trimCachedQueries(
  queryClient: QueryClient,
  cachedQueries: [QueryKey, InfiniteData<CursorPage<CustomerRow>> | undefined][]
): void {
  for (const [cachedKey, data] of cachedQueries) {
    const trimmed = trimCustomerListCache(data);
    if (trimmed !== data) {
      queryClient.setQueryData(cachedKey, trimmed);
    }
  }
}
